/**
 * Admin — Live production-readiness dashboard.
 *
 * Calls /api/admin/readiness and renders a grouped checklist. Each check
 * has a severity (pass / warn / fail) and a short reason.
 */
import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";

const ICONS = { pass: CheckCircle2, warn: AlertTriangle, fail: XCircle };
const COLORS = {
  pass: "#10B981",
  warn: "#F59E0B",
  fail: "#EF4444",
};
const VERDICT = {
  ready: { color: "#10B981", label: "Ready" },
  attention: { color: "#F59E0B", label: "Needs attention" },
  blocked: { color: "#EF4444", label: "Blocked" },
};

export default function AdminReadiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/readiness");
      setData(r.data);
    } catch {
      setData({ verdict: "blocked", counts: { pass: 0, warn: 0, fail: 1 }, groups: {} });
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const v = data ? VERDICT[data.verdict] || VERDICT.attention : null;

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="fl-label text-[#f47a20]">Admin · Phase H</div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="fl-heading text-4xl md:text-5xl mt-2">Production readiness</h1>
          <button
            onClick={refresh}
            disabled={loading}
            className="fl-btn-anim fl-btn text-sm"
            data-testid="readiness-refresh"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Re-run checks
          </button>
        </div>

        <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
          Live checks across environment, persistence, seed data, authentication hygiene,
          clinical guardrails, portability documentation and third-party integrations.
        </p>

        {data && (
          <div className="mt-6 fl-card p-5 flex flex-wrap items-center gap-6" data-testid="readiness-summary">
            <div>
              <div className="fl-label">Overall</div>
              <div className="fl-heading text-3xl mt-1" style={{ color: v.color }} data-testid="readiness-verdict">
                {v.label}
              </div>
            </div>
            <div className="flex gap-6 text-sm">
              <div><span className="text-[#10B981] fl-heading text-2xl">{data.counts.pass}</span><div className="text-xs text-zinc-500">pass</div></div>
              <div><span className="text-[#F59E0B] fl-heading text-2xl">{data.counts.warn}</span><div className="text-xs text-zinc-500">warn</div></div>
              <div><span className="text-[#EF4444] fl-heading text-2xl">{data.counts.fail}</span><div className="text-xs text-zinc-500">fail</div></div>
            </div>
            <div className="text-xs text-zinc-500 ml-auto">
              Generated {(data.generated_at || "").slice(11, 19)} UTC
            </div>
          </div>
        )}

        {data && Object.entries(data.groups).map(([groupName, checks]) => (
          <div key={groupName} className="mt-6">
            <div className="fl-label text-[#f47a20] mb-3 uppercase tracking-widest">{groupName}</div>
            <div className="space-y-1" data-testid={`readiness-group-${groupName}`}>
              {checks.map((c) => {
                const Icon = ICONS[c.severity] || AlertTriangle;
                return (
                  <div key={c.name} className="fl-card p-3 flex items-start gap-3" data-testid={`readiness-check-${c.name}`}>
                    <Icon size={16} className="mt-0.5 shrink-0" style={{ color: COLORS[c.severity] }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm">{c.name}</div>
                      <div className="text-xs text-zinc-500">{c.message}</div>
                    </div>
                    <div className="fl-label text-[10px]" style={{ color: COLORS[c.severity] }}>{c.severity}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-10 fl-card p-4 text-xs text-zinc-500">
          These checks are computed live against the running stack. See
          <a className="text-[#f47a20] hover:underline ml-1" href="/docs/PRODUCTION_READINESS.md">/docs/PRODUCTION_READINESS.md</a>
          for the corresponding manual pre-flight checklist.
        </div>
      </div>
    </AdminLayout>
  );
}
